import { useNavigate, useSearch } from '@tanstack/react-router';

/**
 * §13, §14 ТЗ. Фильтры разделов живут в адресе страницы.
 *
 * Так переход из показателя дашборда в отфильтрованный список — обычная
 * ссылка, а не скрытая передача состояния: тот же адрес можно переслать
 * коллеге, положить в закладку и обновить страницу без потери отбора.
 *
 * Ключи перечислены явно: неизвестный параметр в строке запроса отбрасывается,
 * а не уезжает дальше в API.
 */
export const FILTER_KEYS = [
  'projectId',
  'status',
  'search',
  'duplicatesOnly',
  'archived',
  'type',
  'role',
  'channel',
  'periodKey',
  'number',
  'hasIncident',
  'overdueOnly',
  'linkStatus',
  'action',
  'actorId',
  'entityId',
  'expected',
  'command',
  'from',
  'to',
  // Дашборд (§14) держит статусы трёх сущностей одновременно, поэтому у него
  // свои ключи: один `status` на всех означал бы, что фильтр по инциденту
  // молча меняет отбор опросов.
  'managerId',
  'personId',
  'runStatus',
  'incidentStatus',
  'requestType',
  'requestStatus',
] as const;

export type FilterKey = (typeof FILTER_KEYS)[number];
export type SectionSearch = Partial<Record<FilterKey, string>>;

/** Разбор строки запроса раздела. Значения — строки: их читает и `<select>`, и API. */
export function validateSectionSearch(search: Record<string, unknown>): SectionSearch {
  const result: SectionSearch = {};
  for (const key of FILTER_KEYS) {
    const value = search[key];
    if (typeof value === 'string' && value !== '') result[key] = value;
  }
  return result;
}

/**
 * Один фильтр раздела. Сигнатура повторяет `useState`, чтобы раздел не знал,
 * где хранится значение, — меняется только источник.
 *
 * `replace: true`: перебор значений в выпадающем списке не должен превращать
 * кнопку «Назад» в перемотку по каждому нажатию.
 */
export function useUrlFilter(key: FilterKey): [string, (value: string) => void] {
  const search = useSearch({ strict: false }) as SectionSearch;
  const navigate = useNavigate();

  const set = (value: string) => {
    void navigate({
      to: '.',
      search: (previous: SectionSearch) => ({ ...previous, [key]: value || undefined }),
      replace: true,
    });
  };

  return [search[key] ?? '', set];
}

/**
 * Границы дня для фильтра «с … по …» (§13, §14).
 *
 * Верхняя граница — начало следующего дня: сервер сравнивает строго меньше,
 * иначе отбор «с 15.08 по 15.08» означал бы «ровно полночь». Границы считаются
 * в UTC: у объекта в другой зоне край периода смещён на её смещение, и это
 * фильтр отбора, а не отчётный документ.
 */
export function dayStartIso(day: string): string {
  return day ? `${day}T00:00:00.000Z` : '';
}

export function dayEndIso(day: string): string {
  if (!day) return '';
  const next = new Date(`${day}T00:00:00.000Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString();
}

/** Обратное преобразование: из границы периода — день для `<input type="date">`. */
export function isoToDayStart(iso: string): string {
  return iso ? iso.slice(0, 10) : '';
}

export function isoToDayEnd(iso: string): string {
  if (!iso) return '';
  const previous = new Date(iso);
  previous.setUTCDate(previous.getUTCDate() - 1);
  return previous.toISOString().slice(0, 10);
}

/**
 * Последний включённый момент полуинтервала — то, что показывают в подписи
 * периода (§14).
 *
 * Печатать саму границу нельзя: отбор «по 22.08» читался бы как «— 23.08».
 * Вычитать сутки — тоже: без явного «По» верхняя граница равна «сейчас»,
 * и подпись уехала бы на день назад. Верно в обоих случаях ровно одно —
 * граница минус миллисекунда.
 */
export function lastIncludedIso(iso: string): string {
  if (!iso) return '';
  const bound = new Date(iso);
  if (Number.isNaN(bound.getTime())) return iso;
  return new Date(bound.getTime() - 1).toISOString();
}
